/**
 * ノードプール `/node-pools`（A139 の残り）。
 *
 * **これが無いと最初の 1 台を登録できない。** `POST /ops/nodes` は
 * `nodePoolId` 必須なのに、プールを作る画面がどこにも無く、本番の初回
 * セットアップは DB を直接触るしかなかった（開発シードにしかプールが無い状態）。
 *
 * `code` / `region` / `tier` は作成後に変更できない（API 側で弾かれる）。
 * 既に配置済みのノードとドメインの前提（専用プールの所属・ホスト名）が崩れるため。
 * 画面でも編集時はこの 3 つを出さない。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  DataTable,
  Input,
  PageHeader,
  type DataTableColumn,
} from '@mail-sv/ui';
import { labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { ConfirmTextDialog, FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import type { CountedList, NodeGroupSummary } from '../types/api.js';

const PLACEMENT_OPTIONS = [
  { value: 'least_loaded', label: '最も空いているノード' },
  { value: 'round_robin', label: '順番に割り当て' },
  { value: 'pinned', label: '固定（手動で指定）' },
];

const RING_OPTIONS = [
  { value: 'canary', label: 'カナリア' },
  { value: 'ring1', label: 'リング1' },
  { value: 'ring2', label: 'リング2' },
];

export function NodePoolsPage(): React.ReactElement {
  const canWrite = usePermission(OPS_PERMISSION.nodeWrite);
  const canDelete = usePermission(OPS_PERMISSION.nodeDelete);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<NodeGroupSummary | null>(null);
  const [deleting, setDeleting] = React.useState<NodeGroupSummary | null>(null);

  const query = useQuery({
    queryKey: qk.nodeGroups(),
    queryFn: () => api.get<CountedList<NodeGroupSummary>>('/node-groups'),
  });

  const items = query.data?.items ?? [];

  const columns: Array<DataTableColumn<NodeGroupSummary>> = [
    {
      id: 'code',
      header: 'コード',
      cell: (p) => <code className="font-mono text-sm font-medium">{p.code}</code>,
    },
    { id: 'name', header: '名前', cell: (p) => p.name },
    { id: 'region', header: 'リージョン', cell: (p) => p.region },
    {
      id: 'tier',
      header: '種別',
      cell: (p) => (
        <Badge variant={p.tier === 'dedicated' ? 'info' : 'muted'} size="sm">
          {p.tier === 'dedicated' ? '専用' : '共用'}
        </Badge>
      ),
    },
    {
      id: 'placement',
      header: '配置方式',
      hideBelow: 'md',
      cell: (p) => PLACEMENT_OPTIONS.find((o) => o.value === p.placementStrategy)?.label ?? p.placementStrategy,
    },
    {
      id: 'hostname',
      header: '既定のメールホスト名',
      hideBelow: 'lg',
      cell: (p) => <code className="font-mono text-xs">{p.defaultMailHostname}</code>,
    },
    {
      id: 'ring',
      header: '更新リング',
      hideBelow: 'md',
      cell: (p) => labelOf(p.updateRing),
    },
    {
      id: 'accepts',
      header: '新規割り当て',
      cell: (p) =>
        p.acceptsNewAllocations ? (
          <Badge variant="success" size="sm">
            受け入れ中
          </Badge>
        ) : (
          <Badge variant="warning" size="sm">
            停止中
          </Badge>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-28',
      cell: (p) => (
        <div className="flex justify-end gap-1">
          {canWrite ? (
            <Button variant="ghost" size="icon-sm" aria-label="編集する" onClick={() => setEditing(p)}>
              <Pencil aria-hidden />
            </Button>
          ) : null}
          {canDelete ? (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="削除する"
              className="text-destructive"
              onClick={() => setDeleting(p)}
            >
              <Trash2 aria-hidden />
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="ノードプール"
        description="サーバーノードを束ねる単位。リージョン・配置方式・更新リングの既定値をここで決めます。"
        actions={
          <>
            <RefetchingDot active={query.isFetching && !query.isLoading} />
            {canWrite ? (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus aria-hidden />
                プールを作成
              </Button>
            ) : null}
          </>
        }
      />

      {!query.isLoading && items.length === 0 ? (
        <Alert variant="warning" className="mb-4">
          <AlertDescription>
            プールが 1 つもありません。<strong>プールが無いとサーバーノードを 1 台も登録できません</strong>
            （ノードの登録はプールの指定が必須です）。まずここでプールを作成してください。
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(p) => p.id}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="ノードプール" />}
      />

      <CreatePoolDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditPoolDialog pool={editing} onClose={() => setEditing(null)} />
      <DeletePoolDialog pool={deleting} onClose={() => setDeleting(null)} />
    </>
  );
}

/* ------------------------------------------------------------- 作成 */

function CreatePoolDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [code, setCode] = React.useState('');
  const [name, setName] = React.useState('');
  const [region, setRegion] = React.useState('');
  const [tier, setTier] = React.useState('shared');
  const [dedicatedOrganizationId, setDedicatedOrganizationId] = React.useState('');
  const [placementStrategy, setPlacementStrategy] = React.useState('least_loaded');
  const [defaultMailHostname, setDefaultMailHostname] = React.useState('');
  const [acceptsNewAllocations, setAcceptsNewAllocations] = React.useState(true);
  const [updateRing, setUpdateRing] = React.useState('ring1');

  React.useEffect(() => {
    if (!open) return;
    setCode('');
    setName('');
    setRegion('');
    setTier('shared');
    setDedicatedOrganizationId('');
    setPlacementStrategy('least_loaded');
    setDefaultMailHostname('');
    setAcceptsNewAllocations(true);
    setUpdateRing('ring1');
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      api.post('/node-groups', {
        code: code.trim(),
        name: name.trim(),
        region: region.trim(),
        tier,
        // `.strict()` なので、共用プールに `dedicatedOrganizationId` を
        // 付けたまま送らない（未知キーではないが、意味の無い値を残さない）。
        ...(tier === 'dedicated' ? { dedicatedOrganizationId: dedicatedOrganizationId.trim() } : {}),
        placementStrategy,
        defaultMailHostname: defaultMailHostname.trim().toLowerCase(),
        acceptsNewAllocations,
        updateRing,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.nodeGroups() });
      onOpenChange(false);
    },
  });

  // サーバー側の zod と同じ条件で先に止める（コードは英小文字・数字・ハイフン）。
  const codeOk = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(code.trim()) && code.trim().length >= 2;
  const invalid =
    !codeOk ||
    name.trim().length === 0 ||
    region.trim().length === 0 ||
    defaultMailHostname.trim().length < 3 ||
    (tier === 'dedicated' && dedicatedOrganizationId.trim().length === 0);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="ノードプールを作成する"
      description="コード・リージョン・種別は作成後に変更できません。"
      submitLabel="作成する"
      pending={submit.isPending}
      error={submit.error}
      disabled={invalid}
      onSubmit={() => submit.mutate()}
      wide
    >
      <div className="grid grid-cols-2 gap-3">
        <Field
          label="コード"
          required
          hint="英小文字・数字・ハイフンのみ（2 文字以上）。ホスト名やラベルに出ます。"
          error={code.length > 0 && !codeOk ? '使用できない文字が含まれています' : undefined}
        >
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase())}
            placeholder="tokyo-shared"
            spellCheck={false}
          />
        </Field>
        <Field label="名前" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="東京 共用プール" />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="リージョン" required>
          <Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="jp-tokyo" spellCheck={false} />
        </Field>
        <Field label="種別" required>
          <NativeSelect
            value={tier}
            onChange={setTier}
            options={[
              { value: 'shared', label: '共用' },
              { value: 'dedicated', label: '専用（1 組織に固定）' },
            ]}
          />
        </Field>
      </div>

      {tier === 'dedicated' ? (
        <Field label="割り当て先の組織 ID" required>
          <Input
            value={dedicatedOrganizationId}
            onChange={(e) => setDedicatedOrganizationId(e.target.value)}
            spellCheck={false}
          />
        </Field>
      ) : null}

      <Field
        label="既定のメールホスト名"
        required
        hint="このプールのノードが名乗る既定のホスト名。MX / HELO に使われます。"
      >
        <Input
          value={defaultMailHostname}
          onChange={(e) => setDefaultMailHostname(e.target.value)}
          placeholder="mail.example.com"
          spellCheck={false}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="配置方式" required>
          <NativeSelect value={placementStrategy} onChange={setPlacementStrategy} options={PLACEMENT_OPTIONS} />
        </Field>
        <Field label="更新リング" required hint="アップデート配信の順番。カナリアから順に広げます。">
          <NativeSelect value={updateRing} onChange={setUpdateRing} options={RING_OPTIONS} />
        </Field>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={acceptsNewAllocations}
          onChange={(e) => setAcceptsNewAllocations(e.target.checked)}
          className="size-4 rounded border-border"
        />
        新規の割り当てを受け入れる
      </label>
    </FormDialog>
  );
}

/* ------------------------------------------------------------- 編集 */

function EditPoolDialog({
  pool,
  onClose,
}: {
  pool: NodeGroupSummary | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [name, setName] = React.useState('');
  const [placementStrategy, setPlacementStrategy] = React.useState('least_loaded');
  const [defaultMailHostname, setDefaultMailHostname] = React.useState('');
  const [acceptsNewAllocations, setAcceptsNewAllocations] = React.useState(true);
  const [updateRing, setUpdateRing] = React.useState('ring1');

  React.useEffect(() => {
    if (!pool) return;
    setName(pool.name);
    setPlacementStrategy(pool.placementStrategy);
    setDefaultMailHostname(pool.defaultMailHostname);
    setAcceptsNewAllocations(pool.acceptsNewAllocations);
    setUpdateRing(pool.updateRing);
  }, [pool]);

  const submit = useMutation({
    mutationFn: () =>
      // 差分のあった項目だけ送る（`.strict()` なので未知キーは 400）。
      api.patch(`/node-groups/${pool?.id ?? ''}`, {
        ...(pool && name !== pool.name ? { name: name.trim() } : {}),
        ...(pool && placementStrategy !== pool.placementStrategy ? { placementStrategy } : {}),
        ...(pool && defaultMailHostname !== pool.defaultMailHostname
          ? { defaultMailHostname: defaultMailHostname.trim().toLowerCase() }
          : {}),
        ...(pool && acceptsNewAllocations !== pool.acceptsNewAllocations ? { acceptsNewAllocations } : {}),
        ...(pool && updateRing !== pool.updateRing ? { updateRing } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.nodeGroups() });
      onClose();
    },
  });

  return (
    <FormDialog
      open={pool !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="ノードプールを編集する"
      description={pool ? `${pool.code}（コード・リージョン・種別は変更できません）` : undefined}
      submitLabel="保存する"
      pending={submit.isPending}
      error={submit.error}
      disabled={name.trim().length === 0 || defaultMailHostname.trim().length < 3}
      onSubmit={() => submit.mutate()}
      wide
    >
      <Field label="名前" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <Field label="既定のメールホスト名" required>
        <Input
          value={defaultMailHostname}
          onChange={(e) => setDefaultMailHostname(e.target.value)}
          spellCheck={false}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="配置方式" required>
          <NativeSelect value={placementStrategy} onChange={setPlacementStrategy} options={PLACEMENT_OPTIONS} />
        </Field>
        <Field label="更新リング" required>
          <NativeSelect value={updateRing} onChange={setUpdateRing} options={RING_OPTIONS} />
        </Field>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={acceptsNewAllocations}
          onChange={(e) => setAcceptsNewAllocations(e.target.checked)}
          className="size-4 rounded border-border"
        />
        新規の割り当てを受け入れる
      </label>
    </FormDialog>
  );
}

/* ------------------------------------------------------------- 削除 */

function DeletePoolDialog({
  pool,
  onClose,
}: {
  pool: NodeGroupSummary | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api.del(`/node-groups/${pool?.id ?? ''}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.nodeGroups() });
      onClose();
    },
  });

  return (
    <ConfirmTextDialog
      open={pool !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="ノードプールを削除する"
      description="所属しているノードが 1 台でも残っている場合は削除できません（先にノードを移してください）。"
      confirmText={pool?.code ?? ''}
      confirmLabel="削除する"
      pending={remove.isPending}
      error={remove.error}
      onConfirm={() => remove.mutate()}
    >
      <Alert variant="warning">
        <AlertDescription>
          <Boxes className="mr-1 inline size-4" aria-hidden />
          削除するとこのプールを既定にしていた配置設定が失われます。
        </AlertDescription>
      </Alert>
    </ConfirmTextDialog>
  );
}
